import { PlaceableDevice } from 'src/pages/PlannerPage/Planner/dialogs/ReplaceDeviceDialog/placeable-device';
import { getDeviceById, getDistinctGroupsOfDevs, getDistinctSerialConnectionsOfDevs, getGroupOfDevice } from "src/components/Planner/planner/uDev";
import { chainChangeDevices } from "src/components/Planner/planner/uDev-dom";
import { isDevLightingSwitch, isDevShadingType } from 'src/components/Planner/planner/grouping';
import { ChangeDevicesAction } from 'src/components/Planner/planner/history/device-actions';
import { PlannerGlobals } from 'src/components/Planner/planner/planner-globals';

export enum ReplacementScope {
    SINGLE = 'SINGLE',
    ROOM = 'ROOM',
    FLOOR = 'FLOOR',
}

export enum IncompatibilityReason {
    SHADING_TYPE_MISMATCH = 'SHADING_TYPE_MISMATCH',
    LIGHTING_SWITCH_MISMATCH = 'LIGHTING_SWITCH_MISMATCH',
    GROUPING_INCOMPATIBLE = 'GROUPING_INCOMPATIBLE',
}

export interface CompatibilityResult {
    allowed: boolean;
    reason?: IncompatibilityReason;
}

export function checkGroupingCompatibility(
    srcDevice: any,
    targetDevice: PlaceableDevice
): CompatibilityResult {
    if (isDevShadingType(srcDevice) && !isDevShadingType(targetDevice)) {
        return { allowed: false, reason: IncompatibilityReason.SHADING_TYPE_MISMATCH };
    }
    if (isDevLightingSwitch(srcDevice) && !isDevLightingSwitch(targetDevice)) {
        return { allowed: false, reason: IncompatibilityReason.LIGHTING_SWITCH_MISMATCH };
    }
    const groupingDisallowed =
        (srcDevice.groupable && (!targetDevice.groupable || srcDevice.is_loxone !== targetDevice.is_loxone)) ||
        isPwmToTree(srcDevice, targetDevice);
    if (groupingDisallowed) {
        return { allowed: false, reason: IncompatibilityReason.GROUPING_INCOMPATIBLE };
    }
    return { allowed: true };
}


function isPwmToTree(srcDevice: any, targetDevice: PlaceableDevice): boolean {
    if (srcDevice.user_custom_item_id) return false;
    const srcP = +srcDevice.product_name_id;
    const tarP = +targetDevice.id.replace('product_', '').replace('user_custom_item_', '');
    return [102, 208].includes(srcP) && [107].includes(tarP);
}

export function executeChange(
    deviceId: number,
    targetDevice: PlaceableDevice,
    scope: ReplacementScope,
    onProgress: (percent: number) => void,
    onComplete: (success: boolean) => void
): void {
    const device = getDeviceById(deviceId);
    if (!device) {
        onComplete(false);
        return;
    }

    const devicesToChange = getDevicesForScope(deviceId, scope, device);
    if (devicesToChange.length === 0) {
        onComplete(false);
        return;
    }

    const targetProdId = getTargetProductId(targetDevice);
    const targetIsCustom = isTargetCustom(targetDevice);
    const srcProdId = getSourceProdId(device);
    const srcIsCustom = isSourceCustom(device);
    const groupingCheck = checkGroupingCompatibility(device, targetDevice);

    const changeParams = devicesToChange.map((d: any) => ({
        srcDeviceId: d.deviceId,
        targetProductId: targetProdId,
        targetIdIsCustom: targetIsCustom,
        forcedPosition: null,
    }));

    const groups = getDistinctGroupsOfDevs(devicesToChange);
    const serConns = getDistinctSerialConnectionsOfDevs(devicesToChange);
    const deviceIds = devicesToChange.map((d: any) => d.deviceId);

    chainChangeDevices(
        changeParams,
        0,
        (success: boolean, deviceIdToSerialParams: any) => {
            if (success) {
                const action = new ChangeDevicesAction(
                    deviceIds,
                    groups,
                    srcProdId,
                    targetProdId,
                    srcIsCustom,
                    targetIsCustom,
                    !groupingCheck.allowed,
                    serConns,
                    deviceIdToSerialParams
                );
                PlannerGlobals.plannerHistoryManager?.addHistoryEntry(action);
            }
            onComplete(success);
        },
        onProgress,
        !groupingCheck.allowed
    );
}

function getDevicesForScope(
    deviceId: number,
    scope: ReplacementScope,
    device: any
): any[] {
    const allDevices = PlannerGlobals.devices ?? [];
    const srcProdId = getSourceProdId(device);
    const srcIsCustom = isSourceCustom(device);

    switch (scope) {
        case ReplacementScope.SINGLE: {
            const group = getGroupOfDevice(deviceId);
            if (group) {
                return group.deviceIds
                .map((id: number) => getDeviceById(id))
                .filter(Boolean);
            }
            return [device];
        }
        case ReplacementScope.ROOM: {
            return allDevices.filter((d: any) => {
                if (d.roomId !== device.roomId) return false;
                if (srcIsCustom) return d.user_custom_item_id === srcProdId;
                return d.product_name_id === srcProdId && !d.user_custom_item_id;
            });
        }
        case ReplacementScope.FLOOR: {
            return allDevices.filter((d: any) => {
                if (srcIsCustom) return d.user_custom_item_id === srcProdId;
                return d.product_name_id === srcProdId && !d.user_custom_item_id;
            });
        }
        default:
            return [device];
    }
}

function getTargetProductId(targetDevice: PlaceableDevice): number {
    return +targetDevice.id.replace('product_', '').replace('user_custom_item_', '');
}

function isTargetCustom(targetDevice: PlaceableDevice): boolean {
    return targetDevice.id.startsWith('user_custom_item');
}

function getSourceProdId(device: any): number {
    return device.user_custom_item_id ?? device.product_name_id;
}

function isSourceCustom(device: any): boolean {
    return !!device.user_custom_item_id;
}
